import { useCallback, useEffect, useRef, useState } from 'react'

type ExportPhase = 'idle' | 'preparing' | 'downloading' | 'completed'

interface UseDocExportProgressOptions {
	resetDelayMs?: number
	subscribe?: boolean
}

interface DocExportProgressState {
	phase: ExportPhase
	percent: number | null
}

const listeners = new Set<(state: DocExportProgressState) => void>()
let sharedState: DocExportProgressState = {
	phase: 'idle',
	percent: null,
}
let sharedResetTimeout: ReturnType<typeof setTimeout> | null = null
let sharedRequestId = 0

const emitState = () => {
	listeners.forEach(listener => listener(sharedState))
}

const setSharedState = (nextState: DocExportProgressState) => {
	sharedState = nextState
	emitState()
}

export function useDocExportProgress(options: UseDocExportProgressOptions = {}) {
	const { resetDelayMs = 1200, subscribe = true } = options
	const [state, setState] = useState<DocExportProgressState>(sharedState)
	const resetDelayRef = useRef(resetDelayMs)

	useEffect(() => {
		resetDelayRef.current = resetDelayMs
	}, [resetDelayMs])

	const clearResetTimer = useCallback(() => {
		if (sharedResetTimeout) {
			clearTimeout(sharedResetTimeout)
			sharedResetTimeout = null
		}
	}, [])

	const begin = useCallback(() => {
		clearResetTimer()
		sharedRequestId += 1
		setSharedState({
			phase: 'preparing',
			percent: null,
		})
		return sharedRequestId
	}, [clearResetTimer])

	const updateDownloadProgress = useCallback((loaded: number, total?: number, requestId?: number) => {
		if (requestId && requestId !== sharedRequestId) {
			return
		}
		let nextPercent: number | null = null
		if (!total || total <= 0) {
			nextPercent = null
		} else {
			const ratio = Math.max(0, Math.min(1, loaded / total))
			nextPercent = Math.round(ratio * 100)
		}
		setSharedState({
			phase: 'downloading',
			percent: nextPercent,
		})
	}, [])

	const complete = useCallback(
		(requestId?: number) => {
			if (requestId && requestId !== sharedRequestId) {
				return
			}
			clearResetTimer()
			setSharedState({
				phase: 'completed',
				percent: 100,
			})
			sharedResetTimeout = setTimeout(() => {
				setSharedState({
					phase: 'idle',
					percent: null,
				})
			}, resetDelayRef.current)
		},
		[clearResetTimer]
	)

	const fail = useCallback(
		(requestId?: number) => {
			if (requestId && requestId !== sharedRequestId) {
				return
			}
			clearResetTimer()
			setSharedState({
				phase: 'idle',
				percent: null,
			})
		},
		[clearResetTimer]
	)

	const track = useCallback(
		async <T>(requestFactory: (onProgress: (loaded: number, total?: number) => void) => Promise<T>) => {
			const requestId = begin()
			try {
				const result = await requestFactory((loaded, total) => updateDownloadProgress(loaded, total, requestId))
				complete(requestId)
				return result
			} catch (error) {
				fail(requestId)
				throw error
			}
		},
		[begin, complete, fail, updateDownloadProgress]
	)

	useEffect(() => {
		if (!subscribe) {
			return
		}
		const listener = (nextState: DocExportProgressState) => {
			setState(nextState)
		}
		listeners.add(listener)
		setState(sharedState)
		return () => {
			listeners.delete(listener)
		}
	}, [subscribe])

	const currentState = subscribe ? state : sharedState

	return {
		visible: currentState.phase !== 'idle',
		phase: currentState.phase,
		percent: currentState.percent,
		track,
	}
}
